import { Badge, Group, Stack, Text, Tooltip } from '@mantine/core'

/**
 * A coluna "Capacidades" da tela de Cobertura de Acesso AWS.
 *
 * **O que ela conserta.** Até 19/08/2026 a tela respondia *"consigo assumir o papel?"* e chamava
 * isso de `OK`. Medido na própria tela naquele dia:
 *
 *     commerce-hml  HML ATIVA  448477198791 | READ: OK | ORGACCESS: OK | FINOPS READ: OK ✓
 *
 * — e a aba **Banco de Dados › Performance** daquele tenant **não lia** o Performance Insights,
 * porque faltava `AmazonRDSPerformanceInsightsReadOnly` no papel. A tela dizia OK sobre uma
 * pergunta que ninguém tinha feito.
 *
 * Aqui `OK` significa "as ações que o módulo usa foram medidas e passaram".
 *
 * **Três cores, e a do meio é a que importa:** vermelho para falha medida, **cinza para NÃO
 * medida**, e ausência de badge para quem está bem. Cinza não é "quase verde" — é "a plataforma
 * não conseguiu verificar", e pintar isso de verde é o defeito que este trabalho todo existe para
 * não repetir.
 */

/** Rótulo legível por feature. Feature sem rótulo aparece com o código — melhor que esconder. */
const ROTULOS: Record<string, string> = {
  database_performance: 'Banco de Dados',
  inventory: 'Inventário',
  security_audit: 'Auditoria de segurança',
  load_balancer: 'Load balancer',
  dns: 'DNS',
  storage: 'Armazenamento',
  storage_write: 'Armazenamento (escrita)',
  secrets_read: 'Secrets (leitura)',
  secrets_write: 'Secrets (escrita)',
  remote_exec: 'Execução remota',
  instance_management: 'Gestão de instância',
  waf_provisioning: 'WAF: provisionamento',
  waf_observability: 'WAF: observabilidade',
  waf_logging: 'WAF: log',
  waf_deletion: 'WAF: exclusão',
  waf_rule_management: 'WAF: regras',
  waf_ip_block: 'WAF: bloqueio de IP',
  waf_alerts_sns: 'WAF: alertas',
  waf_alarm_actions: 'WAF: alarmes',
  waf_actions: 'WAF: ações',
}

export function rotuloDaCapacidade(feature: string): string {
  return ROTULOS[feature] ?? feature
}

export function Capacidades({
  falha,
  naoMedidas,
}: {
  falha: string[] | undefined
  naoMedidas: string[] | undefined
}) {
  const comFalha = falha ?? []
  const sem = naoMedidas ?? []

  // Nenhuma das duas listas: ou está tudo medido e OK, ou o tenant nunca foi validado por
  // capacidade. As duas situações são diferentes, e quem sabe distingui-las é o backend — aqui,
  // sem informação, não se afirma nada.
  if (comFalha.length === 0 && sem.length === 0) {
    return (
      <Text size="xs" c="dimmed" data-testid="capacidades-sem-dado">
        —
      </Text>
    )
  }

  return (
    <Stack gap={4}>
      {comFalha.length > 0 && (
        <Group gap={4} wrap="wrap">
          {comFalha.map((f) => (
            <Tooltip
              key={f}
              label={`Ações obrigatórias de ${rotuloDaCapacidade(f)} foram recusadas nesta conta. Expanda a linha para ver quais e como resolver.`}
              multiline
              maw={320}
              withArrow
            >
              <Badge color="red" variant="light" size="sm" data-testid={`capacidade-falha-${f}`}>
                {rotuloDaCapacidade(f)}
              </Badge>
            </Tooltip>
          ))}
        </Group>
      )}
      {sem.length > 0 && (
        <Group gap={4} wrap="wrap">
          {sem.map((f) => (
            <Tooltip
              key={f}
              label={`A plataforma NÃO conseguiu verificar ${rotuloDaCapacidade(f)} nesta conta — não é o mesmo que estar funcionando.`}
              multiline
              maw={320}
              withArrow
            >
              {/* Cinza, e nunca verde: "não medida" é ausência de informação. */}
              <Badge color="gray" variant="light" size="sm" data-testid={`capacidade-nao-medida-${f}`}>
                {rotuloDaCapacidade(f)}: não medida
              </Badge>
            </Tooltip>
          ))}
        </Group>
      )}
    </Stack>
  )
}
